/**
 * TextMatchesBar — every spelling a fuzzy text search used, and the control to strike them out.
 *
 * Fuzzy matching is only safe to default to "loose" because NOTHING IS MATCHED SILENTLY. Typing
 * "badal" also finds "vadal" and "kajal"; this bar shows all three with their counts, and one
 * click strikes out the wrong one. A struck spelling stays on the bar, crossed out, so it can be
 * put back — a strike-out that vanished would be a filter the operator could no longer see.
 *
 * The numbers are the server's, counted in the query's own scope (window, cameras, models), so
 * "kajal 3" means striking it removes about three results — not three somewhere on the estate.
 */
import { ExclamationTriangleIcon, PlusIcon, UserIcon } from '@heroicons/react/24/outline';
import { useState, type ReactNode } from 'react';
import type { SearchFuzziness, SearchTextReport } from '../../api/contracts';

const FUZZINESS: Array<{ value: SearchFuzziness; label: string; hint: string }> = [
  { value: 'exact', label: 'Exact', hint: 'Only the spelling typed (or words starting with it)' },
  { value: 'close', label: 'Close', hint: 'About one mistake — badal finds vadal' },
  { value: 'loose', label: 'Loose', hint: 'About two mistakes — badal finds vadal and kajal' },
];

const KIND_TITLE: Record<string, string> = {
  exact: 'Exactly what was typed',
  prefix: 'Starts with what was typed',
  fuzzy: 'A close spelling',
};

export interface TextMatchesBarProps {
  report: SearchTextReport[] | undefined;
  /** Spellings currently struck out (lower-cased words). */
  excluded: string[];
  fuzziness: SearchFuzziness;
  onToggle: (term: string) => void;
  onFuzziness: (f: SearchFuzziness) => void;
  /** Draw the Exact / Close / Loose control. Off where the page has its own (the one-box panel). */
  showFuzziness?: boolean;
  /** Controls drawn after one word's spellings — the one-box page's "Exact only" and "+ add spelling". */
  tokenExtras?: (token: string) => ReactNode;
}

/** A registered person matched by the word rather than a spelling — struck out as `person:<name>`. */
const PERSON = 'person:';
/** A spelling the operator added to the word themselves — × removes it rather than striking it. */
const ADDED = 'added:';

export default function TextMatchesBar({
  report, excluded, fuzziness, onToggle, onFuzziness, showFuzziness = true, tokenExtras,
}: TextMatchesBarProps) {
  const struck = new Set(excluded.map((e) => e.toLowerCase()));
  // Each token keeps the fields its CLAUSE searched, so a word that cannot live there is named
  // as such ("not a number plate") rather than as a word nothing resembled.
  const tokens = (report || []).flatMap((c) => c.tokens.map((t) => ({ ...t, clauseFields: c.fields })));
  const cannotLiveIn = (fields: string[]) => (fields.length === 1 && fields[0] === 'plate'
    ? 'not a number plate — search it under All'
    : fields.length === 1 && fields[0] === 'name'
      ? 'not a name — search it under All'
      : 'cannot be searched here');

  return (
    <div className="flex shrink-0 flex-wrap items-start gap-x-4 gap-y-1.5 border-b border-border bg-card px-4 py-2 text-xs">
      {showFuzziness && (
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Match</span>
          <div className="flex overflow-hidden rounded-md border border-border" role="radiogroup" aria-label="Spelling tolerance">
            {FUZZINESS.map((f) => (
              <button
                key={f.value}
                type="button"
                role="radio"
                aria-checked={fuzziness === f.value}
                title={f.hint}
                onClick={() => onFuzziness(f.value)}
                className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${
                  fuzziness === f.value ? 'bg-brand text-white' : 'text-foreground hover:bg-muted'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {tokens.map((t, i) => (
        <div key={`${t.token}-${i}`} className="flex flex-wrap items-center gap-1">
          <span className="text-muted-foreground">
            “<span className="font-medium text-foreground">{t.token.toLowerCase()}</span>”
            {t.matches.length > 0 && ' →'}
          </span>
          {t.unmatched && (
            <span className="flex items-center gap-1 text-amber-500">
              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
              {t.matches.length
                ? 'every spelling struck out'
                : t.fields.length === 0
                  ? cannotLiveIn(t.clauseFields)
                  : 'nothing close to it in this window'}
            </span>
          )}
          {t.matches.map((m) => {
            const off = m.excluded || struck.has(m.term.toLowerCase());
            const person = m.term.startsWith(PERSON) ? m.term.slice(PERSON.length) : null;
            const added = m.term.startsWith(ADDED) ? m.term.slice(ADDED.length) : null;
            return (
              <button
                key={m.term}
                type="button"
                onClick={() => onToggle(m.term)}
                title={(added
                  ? `"${added}" — a spelling you added, searched exactly. Click to remove it.`
                  : person
                  ? `Registered person ${person} — their sightings are found even where the event has no name.`
                  : `${KIND_TITLE[m.kind] || ''}${m.kind === 'fuzzy' ? ` (${m.distance} edit${m.distance === 1 ? '' : 's'} away)` : ''}`
                    + ` — found as ${m.fields.join(' / ')}.`)
                  + ` ${off ? 'Click to include it again.' : 'Click to strike it out.'}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors ${
                  off
                    ? 'border-border text-muted-foreground line-through opacity-70 hover:opacity-100'
                    : m.kind === 'fuzzy' && !added && !person
                      ? 'border-amber-500/60 text-foreground hover:bg-muted'
                      : 'border-brand-ring bg-brand-lighter text-foreground hover:bg-muted'
                }`}
              >
                {person && <UserIcon className="h-3 w-3 text-muted-foreground" aria-label="registered person" />}
                <span>{added || person || (m.fields.includes('plate') ? m.term.toUpperCase() : m.term)}</span>
                <span className="tabular-nums text-muted-foreground">{m.events}</span>
                <span aria-hidden className="text-muted-foreground">{off ? '+' : '×'}</span>
              </button>
            );
          })}
          {t.matches_total > t.matches.length && (
            <span className="text-muted-foreground">+{t.matches_total - t.matches.length} more</span>
          )}
          {tokenExtras?.(t.token)}
        </div>
      ))}
    </div>
  );
}

/**
 * `text` with every matched word highlighted — how a card shows WHICH word made it a result.
 *
 * Whole words only, case-insensitive, so "badal" lights up in "Badal Tea Stall" but not inside
 * "Badalpur". Plates are compared with separators ignored, because the stored plate has none and
 * the raw read ("GJ182 / T3877") is full of them.
 */
export function highlightWords(text: string, terms: string[]): ReactNode {
  if (!text || !terms.length) return text;
  const wanted = new Set(terms.map((t) => t.toLowerCase()));
  const parts = text.split(/([\p{L}\p{N}]+)/u);
  return parts.map((part, i) => (
    wanted.has(part.toLowerCase())
      // eslint-disable-next-line react/no-array-index-key
      ? <mark key={i} className="rounded bg-brand-light px-0.5 text-foreground">{part}</mark>
      : part
  ));
}

/** Every spelling the answer is matching on (struck-out ones excluded), for `highlightWords`. */
export function activeSpellings(report: SearchTextReport[] | undefined): string[] {
  return (report || []).flatMap((c) => c.tokens.flatMap((t) => t.matches
    .filter((m) => !m.excluded)
    .map((m) => m.term)));
}

/**
 * One typed word's own controls on the one-box page, in the same pill shape as its spellings:
 * "+ add" opens a box to add a spelling of your own (searched exactly, alongside the word), and
 * "exact" stops close and loose spellings of THIS word.
 */
export function WordSpellingControls({
  word, exact, onExact, onAdd,
}: { word: string; exact: boolean; onExact: (on: boolean) => void; onAdd: (spelling: string) => void }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const close = () => { setOpen(false); setDraft(''); };
  const add = () => {
    const v = draft.trim();
    if (v.replace(/[^\p{L}\p{N}]/gu, '').length >= 2) onAdd(v);
    close();
  };
  return (
    <>
      {open ? (
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
            if (e.key === 'Escape') close();
          }}
          onBlur={add}
          placeholder="spelling…"
          aria-label={`Add a spelling to search with “${word}”`}
          className="w-24 rounded-full border border-brand bg-card px-2 py-0.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title={`Also search a spelling of your own with “${word}” — e.g. a name the camera read differently`}
          className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-border px-2 py-0.5 text-muted-foreground transition-colors hover:border-brand hover:text-brand"
        >
          <PlusIcon className="h-3 w-3" />
          add
        </button>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={exact}
        onClick={() => onExact(!exact)}
        title={exact
          ? `“${word}” is matched exactly — click to allow close spellings again`
          : `Match “${word}” exactly — no close or loose spellings`}
        className={`rounded-full px-1.5 py-0.5 text-[11px] transition-colors ${
          exact ? 'bg-brand-light font-medium text-brand' : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        {exact ? '✓ exact' : 'exact'}
      </button>
    </>
  );
}
